import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getTeamOverview, getMemberStats } from '../../api/teamApi';
import { StandardPageSkeleton } from '../../components/common/Skeleton';
import { PageFadeSection } from '../../components/common/PageFadeSection';
import OperisLoader from '../../components/common/OperisLoader';
import { useAuth } from '../../context/AuthContext';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

/* ─────────────────── Helpers ─────────────────── */
const initials = (n) => n ? n.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : '?';
const formatTime = (d) => { if (!d) return ''; return new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); };
const formatDate = (d) => { if (!d) return ''; return new Date(d).toLocaleDateString([], { month: 'short', day: 'numeric' }); };
const formatDayShort = (d) => { if (!d) return ''; return new Date(d).toLocaleDateString([], { weekday: 'short' }); };
const formatDateTime = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const USER_ROLE_BY_INDEX = ['User', 'Employee', 'Lead', 'PM', 'Admin'];

/** Auth user may expose `role` as numeric enum (UserDto) or string. */
const resolveUserRoleName = (user) => {
  if (!user) return '';
  const r = user.role ?? user.Role;
  if (typeof r === 'number' && r >= 0 && r < USER_ROLE_BY_INDEX.length) return USER_ROLE_BY_INDEX[r];
  return typeof r === 'string' ? r : '';
};

/** Labels the daily history strip using API date range (fallback: min/max of points). */
const formatDailyHistoryPeriodLabel = (points, rangeStart, rangeEnd) => {
  const parse = (v) => {
    if (v == null || v === '') return null;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  };
  let start = parse(rangeStart);
  let end = parse(rangeEnd);
  const sorted = [...(points || [])].sort((a, b) => new Date(a.date) - new Date(b.date));
  if (!start && sorted.length) start = parse(sorted[0].date);
  if (!end && sorted.length) end = parse(sorted[sorted.length - 1].date);
  if (!start || !end) return null;
  const s = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const e = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  const dayCount = Math.max(1, Math.round((e.getTime() - s.getTime()) / 86400000) + 1);
  const shortFmt = { month: 'short', day: 'numeric' };
  const sameYear = s.getFullYear() === e.getFullYear();
  const startStr = s.toLocaleDateString(undefined, sameYear ? shortFmt : { ...shortFmt, year: 'numeric' });
  const endStr = e.toLocaleDateString(undefined, { ...shortFmt, year: 'numeric' });
  return `${dayCount}d · ${startStr}–${endStr}`;
};

const formatRecentActivityWindowLabel = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  return `Since ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
};

const EVENT_STYLES = {
  'entry-start': {
    dot: 'bg-cyan-500',
    badge: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-700/50',
    label: 'Start',
  },
  'entry-stop': {
    dot: 'bg-emerald-500',
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700/50',
    label: 'Stop',
  },
  'assignment-created': {
    dot: 'bg-indigo-500',
    badge: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-700/50',
    label: 'Assigned',
  },
  'assignment-completed': {
    dot: 'bg-amber-500',
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-200 dark:border-amber-700/50',
    label: 'Done',
  },
};

const fallbackMemberActivity = (stats) => {
  const entryEvents = (stats?.recentTimeEntries || []).flatMap((entry) => {
    const baseLabel = entry.productionTaskId || entry.description || 'Time entry';
    const startAt = entry.startTime ? new Date(`${(entry.date || '').slice(0, 10)}T${String(entry.startTime).slice(0, 8)}`) : null;
    const stopAt = entry.endTime ? new Date(`${(entry.date || '').slice(0, 10)}T${String(entry.endTime).slice(0, 8)}`) : null;
    return [
      startAt && { eventType: 'entry-start', occurredAt: startAt, title: `${baseLabel} started`, subtitle: stats.fullName, memberName: stats.fullName, productionTaskId: entry.productionTaskId, hours: entry.hours },
      stopAt && { eventType: 'entry-stop', occurredAt: stopAt, title: `${baseLabel} stopped`, subtitle: Number(entry.hours) > 0 ? `${stats.fullName} · ${Number(entry.hours).toFixed(1)}h` : stats.fullName, memberName: stats.fullName, productionTaskId: entry.productionTaskId, hours: entry.hours },
    ].filter(Boolean);
  });
  const assignmentEvents = (stats?.recentAssignments || []).flatMap((a) => [
    a.assignedAt && { eventType: 'assignment-created', occurredAt: a.assignedAt, title: `${a.productionLotName || a.description || 'Task'} assigned`, subtitle: a.assignedByName ? `${stats.fullName} · by ${a.assignedByName}` : stats.fullName, memberName: stats.fullName, productionTaskId: a.productionLotName },
    a.completedAt && { eventType: 'assignment-completed', occurredAt: a.completedAt, title: `${a.productionLotName || a.description || 'Task'} completed`, subtitle: stats.fullName, memberName: stats.fullName, productionTaskId: a.productionLotName },
  ].filter(Boolean));
  return [...entryEvents, ...assignmentEvents].sort((a, b) => new Date(b.occurredAt) - new Date(a.occurredAt));
};

/* ─────────────────── Activity History Panel ─────────────────── */
const ActivityHistoryPanel = ({ title, points = [], dailyHistoryRangeStart, dailyHistoryRangeEnd }) => {
  const safePoints = points?.length ? points : [];
  const maxHours = Math.max(...safePoints.map((p) => Number(p.hours) || 0), 1);
  const periodLabel = formatDailyHistoryPeriodLabel(safePoints, dailyHistoryRangeStart, dailyHistoryRangeEnd);

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h4 className="text-sm font-bold text-gray-900 dark:text-white">{title}</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Hours · Assignments · Starts &amp; Stops</p>
        </div>
        {periodLabel ? (
          <span
            className="text-[10px] font-semibold tracking-wide text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-2.5 py-1 rounded-full border border-indigo-100 dark:border-indigo-800/50 max-w-[55%] text-right leading-tight"
            title="Daily buckets from the server for this chart"
          >
            {periodLabel}
          </span>
        ) : null}
      </div>

      {safePoints.length > 0 ? (
        <div className="overflow-x-auto pb-1">
          <div className="flex gap-2.5 min-w-max">
            {safePoints.map((point) => {
              const hours = Number(point.hours) || 0;
              const heightPct = hours > 0 ? Math.max(6, (hours / maxHours) * 100) : 3;
              return (
                <div key={point.date} className="flex flex-col items-center gap-2 w-[68px]">
                  <div className="w-full h-16 flex items-end justify-center px-1.5">
                    <div
                      className="w-full rounded-t-md bg-gradient-to-t from-indigo-600 to-violet-500 dark:from-indigo-500 dark:to-violet-400 transition-all"
                      style={{ height: `${heightPct}%` }}
                      title={`${hours.toFixed(1)}h`}
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-[11px] font-bold text-gray-700 dark:text-gray-300">{formatDayShort(point.date)}</p>
                    <p className="text-[10px] text-gray-400">{formatDate(point.date)}</p>
                  </div>
                  <div className="w-full rounded-xl bg-gray-50 dark:bg-gray-700/60 border border-gray-100 dark:border-gray-700 px-2 py-2 space-y-1">
                    <div className="flex justify-between text-[10px]">
                      <span className="text-gray-400">Hrs</span>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">{hours.toFixed(1)}</span>
                    </div>
                    <div className="flex justify-between text-[10px]">
                      <span className="text-gray-400">Tasks</span>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">{point.assignments || 0}</span>
                    </div>
                    <div className="flex justify-between text-[10px]">
                      <span className="text-gray-400">S/S</span>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">{point.startedEntries || 0}/{point.stoppedEntries || 0}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="py-10 text-center">
          <p className="text-sm text-gray-400 dark:text-gray-500">No workload history for this period</p>
        </div>
      )}
    </div>
  );
};

/* ─────────────────── Activity Timeline Panel ─────────────────── */
const ActivityTimelinePanel = ({ title, events = [], emptyMessage = 'No recent activity yet.', recentActivitySince }) => {
  const safeEvents = events?.length ? events : [];
  const windowLabel = formatRecentActivityWindowLabel(recentActivitySince);

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h4 className="text-sm font-bold text-gray-900 dark:text-white">{title}</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {[windowLabel, 'Assignment, start & stop events'].filter(Boolean).join(' · ')}
          </p>
        </div>
        <span className="text-[10px] font-semibold uppercase tracking-wider text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/30 px-2.5 py-1 rounded-full border border-violet-100 dark:border-violet-800/50">
          {safeEvents.length} events
        </span>
      </div>

      {safeEvents.length > 0 ? (
        <div className="overflow-y-auto max-h-72 pr-1">
          {safeEvents.map((event, index) => {
            const style = EVENT_STYLES[event.eventType] || EVENT_STYLES['entry-start'];
            return (
              <div key={`${event.eventType}-${event.occurredAt}-${index}`} className="flex gap-3">
                <div className="relative flex flex-col items-center flex-shrink-0">
                  <span className={`mt-1.5 w-2.5 h-2.5 rounded-full ${style.dot} ring-2 ring-white dark:ring-gray-800`} />
                  {index !== safeEvents.length - 1 && (
                    <div className="w-px flex-1 bg-gray-200 dark:bg-gray-700 min-h-[20px] mt-0.5" />
                  )}
                </div>
                <div className="flex-1 pb-3 min-w-0">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 leading-snug">{event.title}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{event.subtitle || event.memberName}</p>
                    </div>
                    <span className={`flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full ${style.badge}`}>{style.label}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className="text-[10px] text-gray-400">{formatDateTime(event.occurredAt)}</span>
                    {event.productionTaskId && (
                      <span className="text-[10px] text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-1.5 py-0.5 rounded-md font-medium border border-indigo-100 dark:border-indigo-800/50">
                        {event.productionTaskId}
                      </span>
                    )}
                    {event.hours != null && Number(event.hours) > 0 && (
                      <span className="text-[10px] text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 px-1.5 py-0.5 rounded-md font-medium border border-emerald-100 dark:border-emerald-800/50">
                        {Number(event.hours).toFixed(1)}h
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="py-10 text-center">
          <p className="text-sm text-gray-400 dark:text-gray-500">{emptyMessage}</p>
        </div>
      )}
    </div>
  );
};

/* ─────────────────── Team trends (past window / last 7d) ─────────────────── */
const TeamTrendsPanel = ({ dailyHistory = [], dailyHistoryRangeStart, dailyHistoryRangeEnd, gradSuffix = '' }) => {
  const sorted = useMemo(
    () => [...(dailyHistory || [])].sort((a, b) => new Date(a.date) - new Date(b.date)),
    [dailyHistory]
  );
  const last7 = sorted.slice(-7);
  const chartData = last7.map((p) => ({
    label: formatDayShort(p.date),
    shortDate: formatDate(p.date),
    hours: Number(p.hours) || 0,
    tasks: Number(p.assignments) || 0,
  }));

  let trendLabel = 'Stable';
  let trendSub = 'Need a few days of history to compare early vs recent pace.';
  if (chartData.length >= 4) {
    const half = Math.floor(chartData.length / 2);
    const earlyAvg = chartData.slice(0, half).reduce((s, x) => s + x.hours, 0) / half;
    const recentAvg =
      chartData.slice(half).reduce((s, x) => s + x.hours, 0) / Math.max(1, chartData.length - half);
    if (recentAvg > earlyAvg * 1.08) {
      trendLabel = 'Incline';
      trendSub = 'Recent days show higher average hours than earlier in this window.';
    } else if (recentAvg < earlyAvg * 0.92) {
      trendLabel = 'Decline';
      trendSub = 'Recent days show lower average hours than earlier in this window.';
    } else {
      trendSub = 'Delivery pace is roughly steady across this window.';
    }
  }

  const taskEarlyRecent = (() => {
    if (chartData.length < 4) return null;
    const half = Math.floor(chartData.length / 2);
    const e = chartData.slice(0, half).reduce((s, x) => s + x.tasks, 0);
    const r = chartData.slice(half).reduce((s, x) => s + x.tasks, 0);
    return { early: e, recent: r };
  })();

  const periodLabel = formatDailyHistoryPeriodLabel(last7, dailyHistoryRangeStart, dailyHistoryRangeEnd);

  const trendBadge =
    trendLabel === 'Incline'
      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-800/50'
      : trendLabel === 'Decline'
        ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200 border border-rose-200 dark:border-rose-800/50'
        : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600';

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h4 className="text-sm font-bold text-gray-900 dark:text-white">Workload trend</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Hours and completed-task signals for the latest stretch (typically up to the last 7 daily buckets).
            {taskEarlyRecent ? ` · Tasks (earlier vs recent segment): ${taskEarlyRecent.early} → ${taskEarlyRecent.recent}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${trendBadge}`}>{trendLabel}</span>
          {periodLabel ? (
            <span className="text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-2.5 py-1 rounded-full border border-indigo-100 dark:border-indigo-800/50">
              {periodLabel}
            </span>
          ) : null}
        </div>
      </div>
      <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">{trendSub}</p>

      {chartData.length > 0 ? (
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} className="text-gray-500" />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} width={36} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} width={28} />
              <Tooltip
                contentStyle={{ borderRadius: 12 }}
                labelFormatter={(_, payload) => payload?.[0]?.payload?.shortDate || ''}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="hours"
                name="Hours"
                stroke="#6366f1"
                fill={`url(#hoursGrad${gradSuffix})`}
                fillOpacity={0.35}
              />
              <Line yAxisId="right" type="monotone" dataKey="tasks" name="Tasks (assignments)" stroke="#db2777" strokeWidth={2} dot={{ r: 3 }} />
              <defs>
                <linearGradient id={`hoursGrad${gradSuffix}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="py-10 text-center text-sm text-gray-400 dark:text-gray-500">No daily history yet for this team.</div>
      )}
    </div>
  );
};

/* ─────────────────── Member Stats Modal ─────────────────── */
const MemberStatsModal = ({ userId, onClose }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    (async () => {
      try {
        const result = await getMemberStats(userId);
        if (result.isSuccess) setStats(result.data);
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [userId]);

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'history', label: 'History' },
    { id: 'activity', label: 'Activity' },
    { id: 'entries', label: 'Time Entries' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <OperisLoader size="md" message="Loading stats..." />
          </div>
        ) : !stats ? (
          <div className="p-8 text-center text-gray-400">Failed to load member stats</div>
        ) : (() => {
          const activityEvents = stats.recentActivity?.length ? stats.recentActivity : fallbackMemberActivity(stats);
          return (
            <>
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-indigo-600 to-violet-600 px-6 py-5 flex-shrink-0">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center ring-2 ring-white/30 flex-shrink-0">
                      <span className="text-white font-bold text-lg">{initials(stats.fullName)}</span>
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-xl leading-tight">{stats.fullName}</h3>
                      <p className="text-indigo-200 text-sm mt-0.5">{stats.email}</p>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span className="text-[11px] font-semibold bg-white/15 text-white px-2 py-0.5 rounded-full">{stats.role}</span>
                        {stats.department && <span className="text-[11px] font-semibold bg-white/15 text-white px-2 py-0.5 rounded-full">{stats.department}</span>}
                      </div>
                    </div>
                  </div>
                  <button onClick={onClose} className="text-white/60 hover:text-white transition p-1 rounded-lg hover:bg-white/10 flex-shrink-0">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${stats.isClockedIn ? 'bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.6)]' : 'bg-gray-400'}`} />
                  <span className="text-white/80 text-sm">
                    {stats.isClockedIn ? `Clocked in since ${formatTime(stats.clockedInSince)}` : 'Not clocked in'}
                  </span>
                  {stats.currentTask && <span className="text-indigo-200 text-xs ml-1">· {stats.currentTask}</span>}
                </div>
              </div>

              {/* Quick Stats Bar */}
              <div className="grid grid-cols-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
                {[
                  { label: 'Today', value: `${Number(stats.totalHoursToday).toFixed(1)}h`, color: 'text-blue-600 dark:text-blue-400' },
                  { label: 'This Week', value: `${Number(stats.totalHoursThisWeek).toFixed(1)}h`, color: 'text-violet-600 dark:text-violet-400' },
                  { label: 'This Month', value: `${Number(stats.totalHoursThisMonth).toFixed(1)}h`, color: 'text-amber-600 dark:text-amber-400' },
                  { label: 'Avg/Day', value: `${Number(stats.averageHoursPerDay).toFixed(1)}h`, color: 'text-emerald-600 dark:text-emerald-400' },
                ].map((s, i) => (
                  <div key={i} className="px-4 py-3 text-center border-r border-gray-100 dark:border-gray-800 last:border-r-0">
                    <p className={`text-base font-bold ${s.color}`}>{s.value}</p>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Tab Bar */}
              <div className="flex border-b border-gray-100 dark:border-gray-800 flex-shrink-0 px-6 gap-1 bg-gray-50 dark:bg-gray-800/50">
                {tabs.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setActiveTab(t.id)}
                    className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
                      activeTab === t.id
                        ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="overflow-y-auto flex-1 p-6">
                {activeTab === 'overview' && (
                  <div className="space-y-5">
                    <div>
                      <h5 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Task Summary</h5>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {[
                          { label: 'Total Assigned', value: stats.totalAssigned, bg: 'bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800/50', text: 'text-blue-600 dark:text-blue-400' },
                          { label: 'Active', value: stats.activeTasks, bg: 'bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-800/50', text: 'text-amber-600 dark:text-amber-400' },
                          { label: 'Completed', value: stats.completedTasks, bg: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-800/50', text: 'text-emerald-600 dark:text-emerald-400' },
                          { label: 'Overdue', value: stats.overdueTasks, bg: 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800/50', text: 'text-red-600 dark:text-red-400' },
                        ].map((s, i) => (
                          <div key={i} className={`${s.bg} border rounded-xl p-3 text-center`}>
                            <p className={`text-2xl font-bold ${s.text}`}>{s.value}</p>
                            <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mt-1">{s.label}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h5 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <svg className="w-3.5 h-3.5 text-violet-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                        Recent Assignments
                      </h5>
                      {stats.recentAssignments?.length > 0 ? (
                        <div className="space-y-2">
                          {stats.recentAssignments.map((a, i) => {
                            const statusMap = {
                              Assigned: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
                              InProgress: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
                              Completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
                              Cancelled: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
                            };
                            return (
                              <div key={i} className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl">
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">{a.productionLotName || a.description || 'Task'}</p>
                                  <p className="text-xs text-gray-400 mt-0.5">by {a.assignedByName || 'Unknown'} · {formatDate(a.assignedAt)}</p>
                                </div>
                                <span className={`ml-3 text-[10px] font-bold px-2.5 py-1 rounded-full flex-shrink-0 ${statusMap[a.status] || statusMap.Assigned}`}>{a.status}</span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400 italic py-4 text-center">No assignments found</p>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'history' && (
                  <ActivityHistoryPanel
                    title="Workload Pulse"
                    points={stats.dailyHistory || []}
                    dailyHistoryRangeStart={stats.dailyHistoryRangeStart}
                    dailyHistoryRangeEnd={stats.dailyHistoryRangeEnd}
                  />
                )}

                {activeTab === 'activity' && (
                  <ActivityTimelinePanel
                    title="Activity Timeline"
                    events={activityEvents}
                    emptyMessage="No assignment or time-entry events recorded yet."
                    recentActivitySince={stats.recentActivitySince}
                  />
                )}

                {activeTab === 'entries' && (
                  <div className="space-y-4">
                    <h5 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-2">
                      <svg className="w-3.5 h-3.5 text-indigo-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      Recent Time Entries
                    </h5>
                    {stats.recentTimeEntries?.length > 0 ? (
                      <div className="space-y-2">
                        {stats.recentTimeEntries.map((e, i) => (
                          <div key={i} className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl">
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              {e.isRunning && <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse flex-shrink-0" />}
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">{e.description || e.productionTaskId || 'Time entry'}</p>
                                <p className="text-xs text-gray-400 mt-0.5">{formatDate(e.date)}{e.startTime ? ` · ${formatTime(e.startTime)}` : ''}{e.endTime ? ` – ${formatTime(e.endTime)}` : ''}</p>
                              </div>
                            </div>
                            <span className="ml-3 text-sm font-bold text-gray-700 dark:text-gray-200 flex-shrink-0">{Number(e.hours).toFixed(1)}h</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic py-8 text-center">No recent time entries</p>
                    )}
                  </div>
                )}
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );
};

/* ─────────────────── Member Row ─────────────────── */
const MemberRow = ({ member, onClickStats }) => (
  <button
    onClick={() => onClickStats(member.userId)}
    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-indigo-50/60 dark:hover:bg-indigo-900/20 transition-colors group text-left"
  >
    <div className="relative flex-shrink-0">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-sm ${
        member.isClockedIn
          ? 'bg-gradient-to-br from-emerald-400 to-green-500 ring-2 ring-green-200 dark:ring-green-800/60'
          : 'bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700'
      }`}>
        {initials(member.fullName)}
      </div>
      <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-gray-900 ${
        member.isClockedIn ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
      }`} />
    </div>

    <div className="min-w-0 flex-1">
      <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{member.fullName}</p>
      <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate">
        {member.isClockedIn
          ? `Clocked in${member.currentTask ? ` · ${member.currentTask}` : ''}`
          : 'Not clocked in'}
        {member.role ? ` · ${member.role}` : ''}
      </p>
    </div>

    <div className="flex items-center gap-2 flex-shrink-0">
      <div className="text-right">
        <p className="text-xs font-bold text-gray-700 dark:text-gray-200">{Number(member.totalHoursToday).toFixed(1)}h</p>
        <p className="text-[9px] text-gray-400 uppercase tracking-wide">today</p>
      </div>
      <div className="hidden sm:flex gap-1">
        <span className="text-[9px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 px-1.5 py-0.5 rounded-full whitespace-nowrap">
          {member.activeTasks} active
        </span>
        <span className="text-[9px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 px-1.5 py-0.5 rounded-full whitespace-nowrap">
          {member.completedTasks} done
        </span>
      </div>
      <svg className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-indigo-500 transition-colors" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
      </svg>
    </div>
  </button>
);

/* ─────────────────── Team Overview Card ─────────────────── */
const TeamOverviewCard = ({ team, onClickStats, cardIndex, userRole }) => {
  const [activeTab, setActiveTab] = useState('members');
  const [headerExpanded, setHeaderExpanded] = useState(true);
  const [memberFilter, setMemberFilter] = useState('all');
  const [activityTypeFilter, setActivityTypeFilter] = useState('all');

  const canUseScopeFilters = ['Lead', 'PM', 'Admin'].includes(userRole || '');

  const clockedInPct = team.totalMembers > 0 ? Math.round((team.clockedInCount / team.totalMembers) * 100) : 0;
  const totalActiveTasks = (team.members || []).reduce((s, m) => s + (Number(m.activeTasks) || 0), 0);
  const totalCompletedTasks = (team.members || []).reduce((s, m) => s + (Number(m.completedTasks) || 0), 0);
  const totalHoursToday = (team.members || []).reduce((s, m) => s + (Number(m.totalHoursToday) || 0), 0);
  const totalOffline = Math.max((team.totalMembers || 0) - (team.clockedInCount || 0), 0);

  const gradId = `ring-grad-${cardIndex}`;

  const tabs = [
    { id: 'members', label: 'Members', count: team.members?.length },
    { id: 'trends', label: 'Trends' },
    { id: 'history', label: 'History' },
    { id: 'activity', label: 'Activity', count: team.recentActivity?.length },
  ];

  const filteredMembers = useMemo(() => {
    const m = team.members || [];
    if (!canUseScopeFilters || memberFilter === 'all') return m;
    return m.filter((member) => {
      switch (memberFilter) {
        case 'clocked':
          return member.isClockedIn;
        case 'active':
          return (Number(member.activeTasks) || 0) > 0;
        case 'done':
          return (Number(member.completedTasks) || 0) > 0;
        case 'hours':
          return (Number(member.totalHoursToday) || 0) > 0;
        default:
          return true;
      }
    });
  }, [team.members, memberFilter, canUseScopeFilters]);

  const filteredActivity = useMemo(() => {
    const ev = team.recentActivity || [];
    if (!canUseScopeFilters || activityTypeFilter === 'all') return ev;
    return ev.filter((e) => e.eventType === activityTypeFilter);
  }, [team.recentActivity, activityTypeFilter, canUseScopeFilters]);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700/80 overflow-hidden hover:shadow-md transition-shadow flex flex-col">
      {/* Top accent stripe */}
      <div className="h-1 bg-gradient-to-r from-indigo-500 via-violet-500 to-purple-500 flex-shrink-0" />

      {/* Card Header */}
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-sm flex-shrink-0">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
              </svg>
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-gray-900 dark:text-white text-base leading-tight truncate">{team.teamName}</h3>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                {team.department && (
                  <span className="inline-flex items-center text-[10px] font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
                    {team.department}
                  </span>
                )}
                {team.leadName && (
                  <span className="inline-flex items-center text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 rounded-full border border-indigo-100 dark:border-indigo-800/50">
                    Lead: {team.leadName}
                  </span>
                )}
                {team.pmName && (
                  <span className="inline-flex items-center text-[10px] font-semibold text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/30 px-2 py-0.5 rounded-full border border-violet-100 dark:border-violet-800/50">
                    PM: {team.pmName}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Clock-in ring */}
            <div className="relative w-11 h-11">
              <svg className="w-11 h-11 -rotate-90" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r="15" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-gray-100 dark:text-gray-800" />
                <circle
                  cx="18" cy="18" r="15" fill="none" stroke={`url(#${gradId})`} strokeWidth="3"
                  strokeLinecap="round" strokeDasharray={`${clockedInPct * 0.942} 100`}
                />
                <defs>
                  <linearGradient id={gradId}>
                    <stop stopColor="#22c55e" />
                    <stop offset="1" stopColor="#10b981" />
                  </linearGradient>
                </defs>
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-gray-700 dark:text-gray-200">
                {team.clockedInCount}/{team.totalMembers}
              </span>
            </div>

            <button
              onClick={() => setHeaderExpanded((e) => !e)}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <svg className={`w-4 h-4 text-gray-400 transition-transform ${headerExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {headerExpanded && (
        <>
          {/* Stats Row */}
          <div className="grid grid-cols-4 border-t border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
            {[
              { label: 'Hours', value: `${totalHoursToday.toFixed(1)}h`, color: 'text-cyan-600 dark:text-cyan-400' },
              { label: 'Active', value: totalActiveTasks, color: 'text-amber-600 dark:text-amber-400' },
              { label: 'Done', value: totalCompletedTasks, color: 'text-emerald-600 dark:text-emerald-400' },
              { label: 'Offline', value: totalOffline, color: 'text-gray-500 dark:text-gray-400' },
            ].map((stat) => (
              <div key={stat.label} className="px-2 py-3 text-center border-r border-gray-100 dark:border-gray-800 last:border-r-0">
                <p className={`text-base font-bold ${stat.color}`}>{stat.value}</p>
                <p className="text-[10px] text-gray-400 uppercase tracking-wide mt-0.5 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Tab Bar */}
          <div className="flex flex-wrap border-b border-gray-100 dark:border-gray-800 px-4 bg-gray-50/70 dark:bg-gray-800/30 flex-shrink-0 gap-x-1">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-4 py-2.5 text-xs font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${
                  activeTab === t.id
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {t.label}
                {t.count != null && (
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                    activeTab === t.id
                      ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-400'
                      : 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                  }`}>
                    {t.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="flex-1 min-h-0">
            {activeTab === 'members' && (
              <div className="px-3 pt-3 pb-2 space-y-2">
                {canUseScopeFilters && (
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { id: 'all', label: 'All' },
                      { id: 'clocked', label: 'Clocked in' },
                      { id: 'active', label: 'Has active tasks' },
                      { id: 'done', label: 'Has completed' },
                      { id: 'hours', label: 'Logged time today' },
                    ].map((chip) => (
                      <button
                        key={chip.id}
                        type="button"
                        onClick={() => setMemberFilter(chip.id)}
                        className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                          memberFilter === chip.id
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-indigo-300'
                        }`}
                      >
                        {chip.label}
                      </button>
                    ))}
                  </div>
                )}
                <div className="max-h-80 overflow-y-auto py-1 space-y-0.5">
                  {filteredMembers?.length > 0 ? (
                    filteredMembers.map((m) => <MemberRow key={m.userId} member={m} onClickStats={onClickStats} />)
                  ) : (
                    <p className="text-sm text-gray-400 text-center py-8 italic">No members match these filters</p>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'trends' && (
              <div className="p-4">
                <TeamTrendsPanel
                  dailyHistory={team.dailyHistory || []}
                  dailyHistoryRangeStart={team.dailyHistoryRangeStart}
                  dailyHistoryRangeEnd={team.dailyHistoryRangeEnd}
                  gradSuffix={`-${cardIndex}`}
                />
              </div>
            )}

            {activeTab === 'history' && (
              <div className="p-4">
                <ActivityHistoryPanel
                  title="Team Delivery Pulse"
                  points={team.dailyHistory || []}
                  dailyHistoryRangeStart={team.dailyHistoryRangeStart}
                  dailyHistoryRangeEnd={team.dailyHistoryRangeEnd}
                />
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="p-4 space-y-3">
                {canUseScopeFilters && (
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { id: 'all', label: 'All events' },
                      { id: 'entry-start', label: 'Starts' },
                      { id: 'entry-stop', label: 'Stops' },
                      { id: 'assignment-created', label: 'Assigned' },
                      { id: 'assignment-completed', label: 'Done' },
                    ].map((chip) => (
                      <button
                        key={chip.id}
                        type="button"
                        onClick={() => setActivityTypeFilter(chip.id)}
                        className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                          activityTypeFilter === chip.id
                            ? 'bg-pink-600 text-white border-pink-600'
                            : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-pink-300'
                        }`}
                      >
                        {chip.label}
                      </button>
                    ))}
                  </div>
                )}
                <ActivityTimelinePanel
                  title="Team Activity Timeline"
                  events={filteredActivity}
                  emptyMessage="No assignment or timing history recorded for this team yet."
                  recentActivitySince={team.recentActivitySince}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/* ─────────────────── Main Page ─────────────────── */
const TeamOverviewPage = () => {
  const { user } = useAuth();
  const userRole = resolveUserRoleName(user);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getTeamOverview();
      if (result.isSuccess) setTeams(result.data || []);
      else setError(result.message || 'Failed to load');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  const searchQuery = search.trim().toLowerCase();
  const filtered = searchQuery
    ? teams
        .map((team) => {
          const teamMatches = team.teamName?.toLowerCase().includes(searchQuery);
          return {
            ...team,
            members: teamMatches
              ? (team.members || [])
              : (team.members || []).filter(
                  (m) =>
                    m.fullName?.toLowerCase().includes(searchQuery) ||
                    m.email?.toLowerCase().includes(searchQuery) ||
                    m.role?.toLowerCase().includes(searchQuery)
                ),
          };
        })
        .filter((team) => team.members?.length > 0 || team.teamName?.toLowerCase().includes(searchQuery))
    : teams;

  const totalMembers = teams.reduce((s, t) => s + t.totalMembers, 0);
  const totalClockedIn = teams.reduce((s, t) => s + t.clockedInCount, 0);
  const totalActive = teams.reduce((s, t) => s + (t.members || []).reduce((ms, m) => ms + (Number(m.activeTasks) || 0), 0), 0);

  if (loading && teams.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-indigo-950/20 py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <StandardPageSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-indigo-950/20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">

        <PageFadeSection>
        {/* Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5 mb-8">
          <div>
            <h1 className="text-3xl font-black text-gray-900 dark:text-white tracking-tight">
              Team
              <span className="ml-2 text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-violet-500">Overview</span>
            </h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
              Monitor team members, clock-in status, and live performance metrics
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 bg-white dark:bg-gray-900 rounded-xl px-4 py-2.5 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                </svg>
              </div>
              <div>
                <p className="text-base font-bold text-gray-900 dark:text-white leading-tight">{teams.length}</p>
                <p className="text-[9px] text-gray-400 uppercase tracking-wider font-medium">Teams</p>
              </div>
            </div>

            <div className="flex items-center gap-2 bg-white dark:bg-gray-900 rounded-xl px-4 py-2.5 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="5" /></svg>
              </div>
              <div>
                <p className="text-base font-bold leading-tight">
                  <span className="text-emerald-600 dark:text-emerald-400">{totalClockedIn}</span>
                  <span className="text-gray-400 text-sm font-normal">/{totalMembers}</span>
                </p>
                <p className="text-[9px] text-gray-400 uppercase tracking-wider font-medium">Clocked In</p>
              </div>
            </div>

            <div className="flex items-center gap-2 bg-white dark:bg-gray-900 rounded-xl px-4 py-2.5 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
                </svg>
              </div>
              <div>
                <p className="text-base font-bold text-amber-600 dark:text-amber-400 leading-tight">{totalActive}</p>
                <p className="text-[9px] text-gray-400 uppercase tracking-wider font-medium">Active Tasks</p>
              </div>
            </div>

            {/* Search */}
            <div className="relative">
              <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              <input
                type="text"
                placeholder="Search teams or members..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 w-64"
              />
            </div>

            {/* Refresh */}
            <button
              onClick={load}
              className="p-2.5 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition shadow-sm"
              title="Refresh"
            >
              <svg className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
              </svg>
            </button>
          </div>
        </div>
        </PageFadeSection>

        <PageFadeSection>
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-400 text-sm flex items-center gap-2">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>
            {error}
          </div>
        )}

        {/* Team Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {filtered.map((team, i) => (
            <TeamOverviewCard
              key={team.teamId}
              team={team}
              onClickStats={setSelectedMember}
              cardIndex={i}
              userRole={userRole}
            />
          ))}
        </div>

        {filtered.length === 0 && !loading && (
          <div className="text-center py-24">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
              </svg>
            </div>
            <p className="text-gray-500 dark:text-gray-400 font-medium text-base">
              {search ? 'No results match your search' : 'No teams available'}
            </p>
            {search && (
              <button onClick={() => setSearch('')} className="mt-3 text-sm text-indigo-500 hover:underline">Clear search</button>
            )}
          </div>
        )}
        </PageFadeSection>
      </div>

      {selectedMember && (
        <MemberStatsModal userId={selectedMember} onClose={() => setSelectedMember(null)} />
      )}
    </div>
  );
};

export default TeamOverviewPage;
